/**
 * The draggable sheet.
 *
 * This is the one surface the user's finger is on for a sustained period, so it
 * is where the fluid-interface rules earn their keep:
 *
 *   - it tracks 1:1 from the point it was grabbed, using pointer capture so it
 *     keeps tracking when the finger leaves the element
 *   - a ~10px threshold has to be crossed before it commits to dragging, so a
 *     tap on the grabber is a tap and not a one-pixel nudge
 *   - it can be grabbed mid-flight and reversed; `animate()` re-targets from the
 *     live presentation value rather than jumping to the logical one
 *   - release velocity is projected forward and the snap point is chosen from
 *     the projection, not from the release position
 *   - that same release velocity is handed to the settle spring *and* to the
 *     dismiss transition, so there is no seam between dragging and animating
 *   - past the top edge it rubber-bands instead of stopping dead
 *   - it enters and exits along the same path, from the bottom
 *
 * Under `prefers-reduced-motion` the whole thing degrades to a cross-fade with
 * no translation, and dragging still works — reduced motion means no vestibular
 * motion, not no direct manipulation.
 */

import { animate, AnimatePresence, motion, useMotionValue } from 'motion/react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DRAG_THRESHOLD_PX,
  FLICK_VELOCITY,
  nearestSnapPoint,
  projectMomentum,
  rubberband,
  springStandard,
  springWithVelocity,
  tapFeedback,
  useReducedMotion,
} from '../design/motion';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name; also rendered as the sheet's heading. */
  title: string;
  children: ReactNode;
  /** A modal sheet dims and pushes back what is behind it. */
  modal?: boolean;
}

export function Sheet({ open, onClose, title, children, modal = true }: SheetProps) {
  const y = useMotionValue(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [dragging, setDragging] = useState(false);

  const grabOffset = useRef(0);
  const grabPoint = useRef(0);
  const committed = useRef(false);
  const samples = useRef<{ y: number; t: number }[]>([]);
  /**
   * The velocity the finger left at, kept so the exit transition can continue
   * at exactly that speed instead of restarting from rest.
   */
  const exitVelocity = useRef(0);

  const reduced = useReducedMotion();

  useEffect(() => {
    if (!open) {
      y.set(0);
      exitVelocity.current = 0;
      return;
    }
    const node = sheetRef.current;
    if (node) setHeight(node.offsetHeight);
  }, [open, children, y]);

  // Escape must always work, and the sheet must never trap focus without a way
  // out — "how do I get out of here" is a question every screen has to answer.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const velocityFromSamples = useCallback(() => {
    const recent = samples.current.slice(-5);
    if (recent.length < 2) return 0;
    const first = recent[0]!;
    const last = recent[recent.length - 1]!;
    const dt = last.t - first.t;
    if (dt <= 0) return 0;
    return ((last.y - first.y) / dt) * 1000;
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Grab from wherever the finger landed. Snapping to a fixed origin here
      // is the fastest way to break the illusion that this is a real object.
      grabOffset.current = event.clientY - y.get();
      grabPoint.current = event.clientY;
      committed.current = false;
      samples.current = [{ y: event.clientY, t: performance.now() }];
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [y],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;

      samples.current.push({ y: event.clientY, t: performance.now() });
      if (samples.current.length > 8) samples.current.shift();

      // Hysteresis: below the threshold this is still a tap, and moving the
      // sheet by three pixels under a resting thumb reads as jitter.
      if (!committed.current) {
        if (Math.abs(event.clientY - grabPoint.current) < DRAG_THRESHOLD_PX) return;
        committed.current = true;
        setDragging(true);
      }

      const raw = event.clientY - grabOffset.current;
      // Downward is free; upward past the open position resists progressively.
      y.set(raw >= 0 ? raw : -rubberband(-raw, height || 400));
    },
    [height, y],
  );

  const settle = useCallback(
    (velocity: number) => {
      // Animate from the *presentation* value — `animate` reads the motion
      // value's current position, so grabbing a settling sheet and throwing it
      // again re-targets continuously instead of snapping.
      animate(y, 0, springWithVelocity(velocity));
    },
    [y],
  );

  const dismiss = useCallback(
    (velocity: number) => {
      exitVelocity.current = velocity;
      tapFeedback('commit');
      onClose();
    },
    [onClose],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const wasDragging = committed.current;
      committed.current = false;
      setDragging(false);
      if (!wasDragging) return;

      const velocity = velocityFromSamples();
      const current = y.get();

      // Decide by the velocity *sign* when the flick was decisive, so the sheet
      // can be thrown shut from anywhere rather than only past a halfway line.
      if (velocity > FLICK_VELOCITY) {
        dismiss(velocity);
        return;
      }
      if (velocity < -FLICK_VELOCITY) {
        settle(velocity);
        return;
      }

      const projected = current + projectMomentum(velocity);
      const target = nearestSnapPoint(projected, [0, height || 400]);
      if (target === 0) settle(velocity);
      else dismiss(velocity);
    },
    [dismiss, height, settle, velocityFromSamples, y],
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          {modal && (
            <motion.div
              className="fixed inset-0 z-40 bg-ink-0/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={springStandard}
              onPointerDown={onClose}
              aria-hidden="true"
            />
          )}

          <motion.div
            ref={sheetRef}
            role="dialog"
            aria-modal={modal}
            aria-label={title}
            className="material-sheet fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-lg rounded-t-(--radius-sheet) px-5 pt-2 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            style={{
              y,
              // Only while motion is imminent: a permanent will-change keeps a
              // compositor layer alive for a surface that is usually static.
              willChange: dragging ? 'transform' : undefined,
            }}
            /*
             * Enter and exit along the same path. A sheet that arrives from the
             * bottom and leaves sideways severs the spatial relationship the
             * user just built.
             *
             * Blur and scale animate together with opacity so the surface reads
             * as a material arriving, not as a rectangle fading in.
             */
            initial={
              reduced
                ? { opacity: 0 }
                : { y: '100%', opacity: 0, scale: 0.98, filter: 'blur(8px)' }
            }
            animate={
              reduced ? { opacity: 1 } : { y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }
            }
            exit={
              reduced ? { opacity: 0 } : { y: '100%', opacity: 0, scale: 0.98, filter: 'blur(6px)' }
            }
            transition={
              reduced
                ? { duration: 0.15 }
                : // The seam between drag and animation: the sheet leaves at the
                  // speed the finger let go of it.
                  springWithVelocity(exitVelocity.current)
            }
          >
            <div
              className="mx-auto flex h-8 w-full cursor-grab touch-none items-center justify-center active:cursor-grabbing"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              role="button"
              tabIndex={0}
              aria-label={`Close ${title}`}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onClose();
              }}
            >
              <span className="h-1 w-9 rounded-full bg-ink-4" aria-hidden="true" />
            </div>

            <h2 className="text-title mb-3 text-ink-7">{title}</h2>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
